import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Notification } from '@magnetic/notification'
import { Spinner } from '@magnetic/spinner'
import { Tag } from '@magnetic/tag'
import { Text } from '@magnetic/text'
import { Divider } from '@magnetic/divider'
import {
  Activity,
  Clock,
  Warning,
  TrendUp,
  WarningCircle,
  ChartBar,
} from '@/lib/icons'
import { getApplicationTrafficMetrics, getApplicationResourceStatus } from '@/lib/api'
import { formatTimestamp, bigIntToNumber } from '@/lib/protobuf-utils'

interface ApplicationTrafficMetricsProps {
  applicationId: string
  applicationName?: string
}

function formatCompactNumber(n: number | bigint | undefined): string {
  if (!n) return '0'
  const num = typeof n === 'bigint' ? Number(n) : n
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function getHealthTagStatus(healthStatus?: string): 'positive' | 'warning' | 'negative' | 'inactive' {
  if (healthStatus === 'Healthy') return 'positive'
  if (healthStatus === 'Degraded') return 'warning'
  if (!healthStatus || healthStatus === 'Unknown') return 'inactive'
  return 'negative'
}

function getSyncTagStatus(syncStatus?: string): 'positive' | 'warning' | 'inactive' {
  if (syncStatus === 'Synced') return 'positive'
  if (!syncStatus || syncStatus === 'Unknown') return 'inactive'
  return 'warning'
}

function getHttpCodeTagStatus(code: string): 'positive' | 'warning' | 'negative' | 'inactive' {
  if (code.startsWith('2')) return 'positive'
  if (code.startsWith('4')) return 'warning'
  if (code.startsWith('5')) return 'negative'
  return 'inactive'
}

function getPodPhaseTagStatus(phase: string): 'positive' | 'warning' | 'negative' | 'inactive' {
  if (phase === 'Running') return 'positive'
  if (phase === 'Pending') return 'warning'
  if (!phase || phase === 'Unknown') return 'inactive'
  return 'negative'
}

export function ApplicationTrafficMetrics({
  applicationId,
}: ApplicationTrafficMetricsProps) {
  const { data: metricsResponse, isLoading: metricsLoading } = useConnectQuery(
    getApplicationTrafficMetrics,
    { applicationId, duration: '24h' },
    {
      refetchInterval: 60000,
    }
  )

  const metrics = metricsResponse

  const { data: resourceStatus, isLoading: statusLoading } = useConnectQuery(
    getApplicationResourceStatus,
    { applicationId },
    {
      refetchInterval: 30000,
    }
  )

  return (
    <Flex direction="vertical" gap="md">
      {/* Traffic Metrics Card */}
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ChartBar size={20} />
            <Heading size="sub-section">Traffic Metrics (24h)</Heading>
          </Flex>

          {metricsLoading ? (
            <Flex align="center" justify="center" style={{ padding: 32 }}>
              <Spinner size="lg" />
            </Flex>
          ) : !metrics?.metricsAvailable ? (
            <Flex direction="vertical" align="center" style={{ padding: 32, opacity: 0.5 }}>
              <WarningCircle size={32} />
              <Text size="sm">{metrics?.message || 'Traffic metrics not available'}</Text>
            </Flex>
          ) : (
            <>
              <Flex gap="lg" wrap>
                {/* Total Requests */}
                <Flex direction="vertical" gap="xs" style={{ flex: '1 1 150px' }}>
                  <Flex align="center" gap="xs" style={{ opacity: 0.7 }}>
                    <TrendUp size={16} />
                    <Text size="sm">Total Requests</Text>
                  </Flex>
                  <Heading size="primary">{formatCompactNumber(metrics.totalRequests)}</Heading>
                </Flex>

                {/* Avg Response Time */}
                <Flex direction="vertical" gap="xs" style={{ flex: '1 1 150px' }}>
                  <Flex align="center" gap="xs" style={{ opacity: 0.7 }}>
                    <Clock size={16} />
                    <Text size="sm">Avg Response Time</Text>
                  </Flex>
                  <Flex align="baseline" gap="xs">
                    <Heading size="primary">{metrics.avgResponseTimeMs.toFixed(0)}</Heading>
                    <Text size="lg" color="light">ms</Text>
                  </Flex>
                </Flex>

                {/* Error Rate */}
                <Flex direction="vertical" gap="xs" style={{ flex: '1 1 150px' }}>
                  <Flex align="center" gap="xs" style={{ opacity: 0.7 }}>
                    <Warning size={16} />
                    <Text size="sm">Error Rate</Text>
                  </Flex>
                  <Heading
                    size="primary"
                    style={{
                      color: metrics.errorRatePercent > 5
                        ? 'var(--mds-color-theme-text-error-normal)'
                        : metrics.errorRatePercent > 1
                          ? 'var(--mds-color-theme-text-warning-normal)'
                          : 'var(--mds-color-theme-text-success-normal)'
                    }}
                  >
                    {metrics.errorRatePercent.toFixed(2)}%
                  </Heading>
                </Flex>

                {/* Status Codes */}
                <Flex direction="vertical" gap="xs" style={{ flex: '1 1 200px' }}>
                  <Flex align="center" gap="xs" style={{ opacity: 0.7 }}>
                    <Activity size={16} />
                    <Text size="sm">Status Codes</Text>
                  </Flex>
                  <Flex wrap gap="xs">
                    {Object.entries(metrics.statusCodeCounts)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([code, count]) => (
                        <Tag.Status key={code} status={getHttpCodeTagStatus(code)}>
                          {`${code}: ${formatCompactNumber(bigIntToNumber(count))}`}
                        </Tag.Status>
                      ))}
                  </Flex>
                </Flex>
              </Flex>

              {/* Traffic Graph */}
              {metrics.requestsOverTime.length > 0 && (
                <Flex direction="vertical" gap="sm" style={{ marginTop: 16 }}>
                  <Text size="sm" weight="semi-bold">Requests Over Time</Text>
                  <Flex
                    align="flex-end"
                    justify="space-around"
                    gap="xs"
                    style={{
                      height: 128,
                      backgroundColor: 'var(--mds-color-theme-background-solid-secondary-normal)',
                      borderRadius: 8,
                      padding: 8,
                    }}
                  >
                    {metrics.requestsOverTime.slice(-24).map((dp, i) => {
                      const maxVal = Math.max(...metrics.requestsOverTime.map((d) => d.value))
                      const height = maxVal > 0 ? (dp.value / maxVal) * 100 : 0
                      return (
                        <Flex
                          key={i}
                          style={{
                            flex: 1,
                            height: `${Math.max(height, 2)}%`,
                            backgroundColor: 'var(--mds-color-theme-background-accent-normal)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.2s',
                          }}
                          title={`${formatTimestamp(dp.timestamp)}: ${Number(dp.value).toFixed(0)} req/h`}
                        />
                      )
                    })}
                  </Flex>
                  <Flex justify="space-between">
                    <Text size="xs" color="light">24h ago</Text>
                    <Text size="xs" color="light">Now</Text>
                  </Flex>
                </Flex>
              )}

              {/* Methods Breakdown */}
              {Object.keys(metrics.requestsByMethod).length > 0 && (
                <>
                  <Divider />
                  <Flex direction="vertical" gap="sm">
                    <Text size="sm" weight="semi-bold">Requests by Method</Text>
                    <Flex gap="md">
                      {Object.entries(metrics.requestsByMethod).map(([method, count]) => (
                        <Text key={method} size="sm">
                          {method}
                          {` (${formatCompactNumber(bigIntToNumber(count))})`}
                        </Text>
                      ))}
                    </Flex>
                  </Flex>
                </>
              )}
            </>
          )}
        </Flex>
      </Card>

      {/* Resource Status Card */}
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" justify="space-between">
            <Flex align="center" gap="sm">
              <Activity size={20} />
              <Heading size="sub-section">Deployment Status</Heading>
            </Flex>
            {resourceStatus && (
              <Flex gap="sm">
                <Tag.Status status={getHealthTagStatus(resourceStatus.healthStatus)}>
                  {resourceStatus.healthStatus || 'Unknown'}
                </Tag.Status>
                <Tag.Status status={getSyncTagStatus(resourceStatus.syncStatus)}>
                  {resourceStatus.syncStatus || 'Unknown'}
                </Tag.Status>
              </Flex>
            )}
          </Flex>

          {statusLoading ? (
            <Flex align="center" justify="center" style={{ padding: 32 }}>
              <Spinner size="lg" />
            </Flex>
          ) : !resourceStatus ? (
            <Flex direction="vertical" align="center" style={{ padding: 32, opacity: 0.5 }}>
              <WarningCircle size={32} />
              <Text size="sm">Resource status not available</Text>
            </Flex>
          ) : (
            <Flex direction="vertical" gap="md">
              {/* Sync Error */}
              {resourceStatus.syncError && (
                <Notification status="negative" title="Sync Error">
                  <Text size="sm">{resourceStatus.syncError}</Text>
                </Notification>
              )}

              {/* Health Message */}
              {resourceStatus.healthMessage && (
                <Text size="sm" color="light">
                  {resourceStatus.healthMessage}
                </Text>
              )}

              {/* Pods */}
              {resourceStatus.pods.length > 0 && (
                <Flex direction="vertical" gap="sm">
                  <Text size="sm" weight="semi-bold">Pods</Text>
                  <Flex direction="vertical" gap="xs">
                    {resourceStatus.pods.map((pod) => (
                      <Flex
                        key={pod.name}
                        align="center"
                        justify="space-between"
                        style={{
                          padding: 8,
                          borderRadius: 4,
                          backgroundColor: 'var(--mds-color-theme-background-solid-secondary-normal)',
                        }}
                      >
                        <Flex align="center" gap="sm">
                          <Flex
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: pod.phase === 'Running' && pod.ready
                                ? 'var(--mds-color-theme-text-success-normal)'
                                : pod.phase === 'Pending'
                                  ? 'var(--mds-color-theme-text-warning-normal)'
                                  : 'var(--mds-color-theme-text-error-normal)',
                            }}
                          />
                          <Text size="sm">{pod.name}</Text>
                        </Flex>
                        <Flex gap="sm">
                          <Tag.Status status={getPodPhaseTagStatus(pod.phase)}>
                            {pod.phase}
                          </Tag.Status>
                          {pod.restartCount > 0 && (
                            <Tag.Status status="warning">{`${pod.restartCount} restarts`}</Tag.Status>
                          )}
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              )}

              {/* Recent Events */}
              {resourceStatus.events.length > 0 && (
                <Flex direction="vertical" gap="sm">
                  <Text size="sm" weight="semi-bold">Recent Events</Text>
                  <Flex direction="vertical" gap="xs" style={{ maxHeight: 192, overflowY: 'auto' }}>
                    {resourceStatus.events.slice(0, 10).map((event, i) => (
                      <Flex
                        key={i}
                        direction="vertical"
                        style={{
                          padding: 8,
                          borderRadius: 4,
                          backgroundColor: event.type === 'Warning'
                            ? 'var(--mds-color-theme-background-warning-normal)'
                            : 'var(--mds-color-theme-background-solid-secondary-normal)',
                        }}
                      >
                        <Flex align="center" gap="sm">
                          {event.type === 'Warning' && (
                            <Warning size={16} style={{ color: 'var(--mds-color-theme-text-warning-normal)' }} />
                          )}
                          <Text size="sm" weight="semi-bold">{event.reason}</Text>
                          {event.count > 1 && (
                            <Tag>{`x${event.count}`}</Tag>
                          )}
                        </Flex>
                        <Text size="sm" color="light" style={{ marginTop: 4 }}>
                          {event.message}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
